// @flow
import { formatPrice } from '@src/utils/general';
import { useCallback, useContext, useState } from 'react';

import * as routes from '@src/app_routes/bands';

import { BandStateContext } from '@contexts/band';

import Loading from '@components/icons/Loading';

import css from '@styles/productDetail/otherMetals';

type TListItem = {
  metal: string,
  humanized_metal: string,
  price: number,
  vendor_sku: string
};

type TIconProps = {
  metal: string,
  humanizedMetal: string
};

const blackIcon = '/images/metal-color-icons/black.png';
const whiteIcon = '/images/metal-color-icons/white.png';
const yellowIcon = '/images/metal-color-icons/yellow.png';
const roseIcon = '/images/metal-color-icons/rose.png';

const metals = {
  '14KY': yellowIcon,
  '14KW': whiteIcon,
  '14KR': roseIcon,
  '14KY_14KW': yellowIcon,
  '14KY_14KR': yellowIcon,
  '14KW_14KY': whiteIcon,
  '14KW_14KR': whiteIcon,
  '14KR_14KY': roseIcon,
  '14KR_14KW': roseIcon,
  '18KY': yellowIcon,
  '18KW': whiteIcon,
  '18KR': roseIcon,
  '18KY_18KW': yellowIcon,
  '18KY_18KR': yellowIcon,
  '18KW_18KY': whiteIcon,
  '18KW_18KR': whiteIcon,
  '18KR_18KY': roseIcon,
  '18KR_18KW': roseIcon,
  PLAT: whiteIcon,
  TITANIUM: whiteIcon,
  BLACKTITANIUM: blackIcon
};

function OtherMetalIcon ({ metal, humanizedMetal }: TIconProps) {
  const icon = metals[metal];

  if (!icon) return null;

  return <img src={icon} alt={humanizedMetal} />;
}

function OtherMetalListItem ({
  item,
  onClick,
  priceDifference
}: {
  item: TListItem,
  onClick?: (sku: string) => void,
  priceDifference: number
}) {
  const [loading, setLoading] = useState(false);
  const handleClick = useCallback(
    (e) => {
      if (!onClick) return;

      e.preventDefault();
      setLoading(true);
      onClick(item.vendor_sku);
    },
    [onClick, item.vendor_sku]
  );

  return (
    <div className={css.listItem}>
      <div>
        {loading ? (
          <Loading
            style={{
              maxHeight: 12,
              maxWidth: 12,
              marginLeft: 3,
              marginRight: 13
            }}
          />
        ) : (
          <OtherMetalIcon
            metal={item.metal}
            humanizedMetal={item.humanized_metal}
          />
        )}
      </div>
      <div>
        <a href={routes.detailPath(item)} onClick={handleClick}>
          {item.humanized_metal}
        </a>
        <br />
        {priceDifference > 0 && `(+ ${formatPrice(Math.abs(priceDifference))})`}
        {priceDifference < 0 && `(- ${formatPrice(Math.abs(priceDifference))})`}
        {priceDifference === 0 && '(same price)'}
      </div>
    </div>
  );
}

function calculatePriceDifference (
  bandPrice: number,
  itemPrice: number,
  includeDiamondInPriceDifference: boolean
) {
  let basePrice;
  let diffPrice;

  itemPrice = parseFloat(itemPrice);
  bandPrice = parseFloat(bandPrice);

  if (includeDiamondInPriceDifference) {
    basePrice = bandPrice;
    diffPrice = itemPrice;
  } else {
    basePrice = bandPrice;
    diffPrice = itemPrice;
  }

  // By rounding, we get the exact value that is displayed on PD pages. We want
  // to do the math based on those numbers, not the pre-rounded numbers. If we
  // do the math based on the pre-rounded numbers, we can be off by a few cents
  // which can result in us showing a difference that is off by one dollar after
  // rounding.
  basePrice = Math.round(basePrice);
  diffPrice = Math.round(diffPrice);

  return diffPrice - basePrice;
}

export default function OtherMetals ({
  onClick,
  includeDiamondInPriceDifference = false
}: {
  onClick?: (sku: string) => void,
  includeDiamondInPriceDifference?: boolean
}) {
  const { band } = useContext(BandStateContext);

  if (!(band.other_metals && band.vendor_sku)) {
    return null;
  }

  const { other_metals: metals } = band;

  return (
    <div className={css.container}>
      <div className={css.title}>Also available in:</div>

      <div className={css.list}>
        {metals.map((item) => (
          <OtherMetalListItem
            item={item}
            priceDifference={calculatePriceDifference(
              band.retail_price,
              item.retail_price,
              includeDiamondInPriceDifference
            )}
            key={item.vendor_sku}
            onClick={onClick}
          />
        ))}
      </div>
    </div>
  );
}
